<!--
  @component
  A row or tile in the asset list, shared by repository folders and external locations. The caller
  provides the asset’s display properties and handles selection, focus and preview.
-->
<script>
  import { Checkbox, GridCell, GridRow, TruncatedText } from '@sveltia/ui';

  import AssetPreview from '$lib/components/assets/shared/asset-preview.svelte';
  import { env } from '$lib/services/user/env.svelte';

  /**
   * @import { Asset, AssetKind, ViewType } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {string} name File name.
   * @property {AssetKind} kind Asset kind.
   * @property {Asset} [asset] Repository asset, whose thumbnail is generated by the CMS.
   * @property {string} [src] Thumbnail URL for an asset on an external service.
   * @property {number} rowIndex Index of the asset in the list, for `aria-rowindex`.
   * @property {ViewType} viewType View type.
   * @property {boolean} selected Whether the asset is selected.
   * @property {boolean} canPreview Whether the asset can be opened in the details overlay.
   * @property {(selected: boolean) => void} onSelectionChange Called when the selection changes.
   * @property {() => void} onFocus Called when the row gets focus.
   * @property {() => void} onPreview Called when the asset is opened by a click on a small screen
   * or a double-click.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    name,
    kind,
    asset = undefined,
    src = undefined,
    rowIndex,
    viewType,
    selected,
    canPreview,
    onSelectionChange,
    onFocus,
    onPreview,
    /* eslint-enable prefer-const */
  } = $props();

  /**
   * Open the details overlay, if the asset can be previewed.
   */
  const preview = () => {
    if (canPreview) {
      onPreview();
    }
  };

  /**
   * Whether a click opens the asset right away, as there is no double-click to wait for: on a
   * small or medium screen, where the selection checkbox is hidden, and on a touch screen of any
   * size, e.g. a tablet in landscape, because Safari never fires `dblclick` for a double tap.
   * `pointerType` tells a tap from a mouse click where the browser dispatches `click` as a
   * `PointerEvent`; elsewhere, the lack of a fine pointer does.
   * @param {MouseEvent} event `click` event.
   * @returns {boolean} Result.
   */
  const opensOnClick = (event) =>
    env.isSmallScreen ||
    env.isMediumScreen ||
    /** @type {PointerEvent} */ (event).pointerType === 'touch' ||
    !env.hasMouse;
</script>

<!-- @todo Add support for drag to move. -->

<GridRow
  aria-rowindex={rowIndex}
  aria-label={name}
  onChange={(event) => {
    onSelectionChange(event.detail.selected);
  }}
  onfocus={onFocus}
  onclick={(/** @type {MouseEvent} */ event) => {
    if (opensOnClick(event)) {
      preview();
    }
  }}
  ondblclick={preview}
>
  {#if !(env.isSmallScreen || env.isMediumScreen)}
    <GridCell class="checkbox">
      <Checkbox
        role="none"
        tabindex="-1"
        checked={selected}
        onChange={({ detail: { checked } }) => {
          onSelectionChange(checked);
        }}
      />
    </GridCell>
  {/if}
  <GridCell class="image">
    <AssetPreview
      {kind}
      {asset}
      {src}
      alt={src ? name : ''}
      variant={viewType === 'list' ? 'icon' : 'tile'}
      cover={env.isSmallScreen}
      checkerboard={kind === 'image'}
    />
  </GridCell>
  {#if !env.isSmallScreen || viewType === 'list'}
    <GridCell class="title">
      <div role="none" class="label">
        <TruncatedText lines={2}>
          <bdi>{name}</bdi>
        </TruncatedText>
      </div>
    </GridCell>
  {/if}
</GridRow>

<style>
  .label {
    word-break: break-all;
  }
</style>
